import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import OrbiSidebar from './orbi/OrbiSidebar';
import { LayoutProvider, useLayout } from '../contexts/LayoutContext';
import { useAuth } from '../contexts/AuthContext';
import FloatingOrbi from './orbi/FloatingOrbi';
import { useOrbiSessions, deleteSession as deleteOrbiSession } from '../hooks/useOrbiSessions';
import { useWorkflowDrafts, deleteDraft as deleteWorkflowDraft } from '../hooks/useWorkflowDraft';
import { hasAdminAccess } from '../lib/admin';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

function Layout() {
  const { hideBottomNav } = useLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();

  // Workflow owns the full viewport — no chrome, no sidebar, no padding.
  const isWorkflow = location.pathname.startsWith('/orbi/workflow');
  // Upload-form only. Narrowed from the earlier three-route allowlist
  // because the user wants Orbi scoped tightly to the one surface
  // where per-field writing help is the whole point.
  const isUploadRoute = location.pathname === '/cms-upload';
  const isOrbi =
    !isWorkflow &&
    (location.pathname === '/orbi' ||
      location.pathname.startsWith('/orbi/') ||
      location.pathname === '/planverse' ||
      location.pathname.startsWith('/planverse/'));

  const displayName = user?.name || user?.email?.split('@')[0] || 'Profile';
  const isAdmin = hasAdminAccess(user?.id, user?.email);
  const { sessions } = useOrbiSessions();
  const workflows = useWorkflowDrafts();
  const activeSessionId =
    location.pathname === '/orbi' ? searchParams.get('session') || undefined : undefined;

  const handleSelectSession = (id: string) => {
    navigate(`/orbi?session=${encodeURIComponent(id)}`);
  };

  const handleDeleteSession = (id: string) => {
    deleteOrbiSession(id);
    // If the deleted session was active, drop back to the empty state.
    if (activeSessionId === id) navigate('/orbi');
  };

  const handleSelectWorkflow = (id: string) => {
    navigate(`/orbi/workflow/${id}`);
  };

  const handleDeleteWorkflow = (id: string) => {
    deleteWorkflowDraft(id);
  };

  const handleNewChat = () => {
    navigate('/orbi');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isWorkflow) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-background">
        {isNative && <div className="h-[env(safe-area-inset-top)] bg-background" />}
        <Outlet />
      </div>
    );
  }

  // FloatingOrbi mounts here so it appears on every authenticated page
  // EXCEPT Workflow (which has its own full composer surface).

  return (
    <div className="min-h-screen bg-background">
      {isNative && <div className="h-[env(safe-area-inset-top)] bg-background" />}

      <div className={`max-w-[1400px] mx-auto flex flex-col md:flex-row ${isNative ? 'min-h-[calc(100vh-env(safe-area-inset-top))]' : 'min-h-screen'}`}>
        {isOrbi ? (
          <>
            <OrbiSidebar
              displayName={displayName}
              userEmail={user?.email}
              userAvatarUrl={user?.avatar_url}
              isAdmin={isAdmin}
              sessions={sessions}
              activeSessionId={activeSessionId}
              workflows={workflows.map((w) => ({ id: w.id, title: w.title, updatedAt: w.updatedAt }))}
              onSelectSession={handleSelectSession}
              onDeleteSession={handleDeleteSession}
              onSelectWorkflow={handleSelectWorkflow}
              onDeleteWorkflow={handleDeleteWorkflow}
              onNewChat={handleNewChat}
              onLogout={handleLogout}
            />
            <div className={hideBottomNav ? 'hidden' : 'md:hidden'}>
              <Sidebar />
            </div>
          </>
        ) : (
          <div className={hideBottomNav ? 'hidden md:block' : ''}>
            <Sidebar />
          </div>
        )}

        <div
          className={`flex-1 flex flex-col min-w-0 md:pb-0 ${
            hideBottomNav
              ? 'pb-0'
              : isNative
                ? 'pb-[calc(4rem+env(safe-area-inset-bottom))]'
                : 'pb-16'
          }`}
        >
          <div className="flex-1 overflow-hidden">
            <div className={isOrbi ? '' : 'p-3 md:p-4 lg:p-5'}>
              <Outlet />
            </div>
          </div>
        </div>
      </div>
      {/* Floating Orbi — Pro only, shown only on upload-related
          routes where per-field help pays off. Hidden elsewhere so
          it isn't a persistent on-every-page overlay. */}
      {!isOrbi && isUploadRoute && <FloatingOrbi />}
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <LayoutProvider>
      <Layout />
    </LayoutProvider>
  );
}
